import React, { useState, useEffect, useRef } from "react";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useMessengerContext } from "../../hooks/useMessengerContext";
import "./MessengerInput.css";
import useSocket from "../../hooks/useSocket";
import { initializeApp, getApps } from "firebase/app";
import { getAuth, getIdToken } from "firebase/auth";
import firebase from "../../Firebase";
import Cookies from "js-cookie";
import { QUICK_REPLY_EVENT } from './Message';

if (!getApps().length) {
  initializeApp(firebase);
}

const MessengerInput = ({ isActive }) => {
  const [newMessage, setNewMessage] = useState("");
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [aiModal, setAiModal] = useState(null); // 'find' | 'schedule'
  const [aiLoading, setAiLoading] = useState(false);
  const { user } = useAuthContext();
  const { state, dispatch } = useMessengerContext();
  const { currentConversation, community } = state;

  const socketFunctions = useSocket(user?.userId, currentConversation?._id);
  const { sendMessage, startTyping, stopTyping } = socketFunctions;
  
  // Clean up debug logging
  // useEffect(() => {
  //   console.log("🔍 [TYPING] Socket functions available:", {
  //     sendMessage: !!sendMessage,
  //     startTyping: !!startTyping,
  //     stopTyping: !!stopTyping,
  //     userId: user?.userId,
  //     conversationId: currentConversation?._id
  //   });
  // }, [sendMessage, startTyping, stopTyping, user?.userId, currentConversation?._id]);
  const [csrfToken, setCsrfToken] = useState(null);
  const [token, setToken] = useState(null);
  const textareaRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  const typingTimeoutRef = useRef(null);
  const typingHeartbeatRef = useRef(null);
  let controller;

  

  // Fetch the CSRF token when the component mounts
  useEffect(() => {
    const fetchCsrfToken = async () => {
      const csrfToken = Cookies.get("mytoken");
      setCsrfToken(csrfToken);
    };

    fetchCsrfToken();
  }, []);

  useEffect(() => {
    const fetchToken = async () => {
      const token = await firebase.auth().currentUser.getIdToken();
      setToken(token);
    };

    fetchToken();
  }, []);

  // Check if mobile, handle keyboard visibility, and auto-focus
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
      
      if (isMobileDevice && textareaRef.current && currentConversation && isActive) {
        // Delay to ensure component is fully rendered and visible
        setTimeout(() => {
          textareaRef.current.focus();
        }, 300);
      }
    };
    
    // Visual Viewport API for keyboard detection
    const handleViewportChange = () => {
      if (window.visualViewport && isMobile) {
        const viewport = window.visualViewport;
        const keyboardHeight = window.innerHeight - viewport.height;
        const input = textareaRef.current?.closest('.chatBoxInput');
        
        if (input) {
          // Adjust input position based on keyboard height
          input.style.bottom = keyboardHeight > 0 ? `${keyboardHeight}px` : '0px';
        }
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Add viewport listener for keyboard detection
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    }
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
    };
  }, [currentConversation, isMobile, isActive]);

  // Cleanup typing timeout and heartbeat on unmount (FIXED - no dependencies)
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (typingHeartbeatRef.current) {
        clearInterval(typingHeartbeatRef.current);
      }
      // Only cleanup on unmount, not on every state change
      stopTyping();
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Clean up typing when conversation changes
  useEffect(() => {
    if (isTyping) {
      console.log("🔄 Conversation changed - stopping typing indicator");
      setIsTyping(false);
      stopTyping();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (typingHeartbeatRef.current) {
        clearInterval(typingHeartbeatRef.current);
        typingHeartbeatRef.current = null;
      }
    }
  }, [currentConversation?._id]); // Removed isTyping and stopTyping from dependencies


  // --- notifyBackend function commented out ---
  // const notifyBackend = async (conversationId, messageId, userId) => {
  //   const controller = new AbortController();
  //   const { signal } = controller;

  //   const newCsrfToken = Cookies.get("mytoken");
  //   if (newCsrfToken !== csrfToken) {
  //     console.log("CSRF token old:", csrfToken);
  //     console.log("CSRF token new:", newCsrfToken);
  //     console.log("messageId 2nd api:", messageId);
  //     console.log("userId 2nd api:", userId);
  //     setCsrfToken(newCsrfToken);
  //   }

  //   console.log("token", token);
  //   console.log("CSRF token new? second api", newCsrfToken);
  //   const requestHeaders = {
  //     Authorization: `Bearer ${token}`,
  //     "X-CSRF-Token": newCsrfToken,
  //   };

  //   try {
  //     console.log("second api happening");

  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_BASE_URL}/messages/lastMessageViewed`,
  //       {
  //         method: "POST",
  //         headers: {
  //           ...requestHeaders,
  //           "Content-Type": "application/json",
  //         },
  //         signal: signal,
  //         body: JSON.stringify({ conversationId, messageId, userId }),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Network second api response was not ok");
  //     }
  //     if (response.ok) {
  //       console.log("response.ok second api update last message", response.ok);
  //     }

  //     const data = await response.json();
  //     console.log(data);
  //   } catch (error) {
  //     console.error("Error:", error);
  //   }
  // };

  // Improved input change handler with heartbeat
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Start typing indicator if not already typing
    if (!isTyping) {
      setIsTyping(true);
      startTyping();
      
      // Start heartbeat to keep typing indicator alive
      typingHeartbeatRef.current = setInterval(() => {
        startTyping();
      }, 2000); // Send heartbeat every 2 seconds
    }
    
    // Clear existing timeout and set new one
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing after 4 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      console.log("🔴 Stopping typing indicator (timeout after 4 seconds)");
      setIsTyping(false);
      stopTyping();
      
      // Clear heartbeat
      if (typingHeartbeatRef.current) {
        clearInterval(typingHeartbeatRef.current);
        typingHeartbeatRef.current = null;
      }
    }, 4000);
  };

  // `textOverride` is a tapped quick reply (see Message.js), sent through
  // exactly the same path as something typed.
  const handleSubmit = async (e, textOverride) => {
    e?.preventDefault?.();
    const draftText = textOverride ?? newMessage;

    if (!draftText.trim()) { 
      return;
    }

    // 🚨 Prevent crash when conversation isn't ready 
    if (!currentConversation) { 
      console.log("⚠️ No conversation selected — cannot send message"); 
      return; 
    }

    if (!currentConversation.members || currentConversation.members.length < 2) { 
      console.log("⚠️ Conversation has no members — cannot send message"); 
      return; 
    } 
    
    if (!community) { 
      console.log("⚠️ Community not loaded yet"); 
      return; 
    }

    // Stop typing when message is sent
    if (isTyping) {
      console.log("🔴 Stopping typing indicator (message sent)");
      setIsTyping(false);
      stopTyping();
      if (typingTimeoutRef.current) 
        clearTimeout(typingTimeoutRef.current);
      if (typingHeartbeatRef.current) {
        clearInterval(typingHeartbeatRef.current);
        typingHeartbeatRef.current = null;
      }
    }

    const receiver = currentConversation.members.find(
      (member) => member !== user.userId
    );

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const optimisticMessage = {
      _id: tempId,
      tempId: tempId,
      sender: user.userId,
      receiver: receiver,
      text: draftText,
      conversationId: currentConversation._id,
      communityId: receiver === community.communityId ? community.communityId : null,
      createdAt: new Date().toISOString(),
      status: 'pending'
    };

    // Clear input immediately for better UX
    const messageText = draftText;
    if (textOverride == null) setNewMessage("");

    // Add optimistic message to UI immediately
    console.log("📝 Adding optimistic message:", optimisticMessage);
    dispatch({ type: "ADD_OPTIMISTIC_MESSAGE", payload: optimisticMessage });

    const message = {
      sender: user.userId,
      receiver: receiver,
      text: messageText,
      conversationId: currentConversation._id,
      communityId: receiver === community.communityId ? community.communityId : null,
    };

    const auth = getAuth();
    const token = await getIdToken(auth.currentUser);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/messages/`,
        {
          method: "POST",
          body: JSON.stringify(message),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("✅ Message saved to DB:", data);

      // Replace optimistic message with server response
      dispatch({ 
        type: "REPLACE_OPTIMISTIC_MESSAGE", 
        payload: { 
          tempId: tempId,
          serverMessage: data
        }
      });

      dispatch({
        type: "UPDATE_LAST_READ_MESSAGE_ID",
        payload: { userId: user.userId, messageId: data._id },
      });

      const messageToReciever = {
        sender: user.userId,
        receiver: currentConversation.members.find(
          (member) => member !== user.userId
        ),
        text: messageText,
        conversationId: currentConversation._id,
        messageId: data._id,
      };

      console.log("📤 Sending message via socket:", messageToReciever);
      sendMessage(messageToReciever);

      // Refocus textarea on mobile to keep keyboard active
      if (isMobile && textareaRef.current) {
        setTimeout(() => {
          textareaRef.current.focus();
        }, 100);
      }

    } catch (err) {
      console.error("Failed to send message:", err);
      
      // Mark message as failed and add retry functionality
      dispatch({ 
        type: "UPDATE_MESSAGE_STATUS", 
        payload: { 
          tempId: tempId,
          updates: { 
            status: 'failed',
            error: err.message,
            retryCount: 0
          }
        }
      });

      // Auto-retry after 2 seconds
      setTimeout(() => {
        retryMessage(tempId, message, messageText);
      }, 2000);

      // Refocus textarea on mobile even when message fails to keep keyboard active
      if (isMobile && textareaRef.current) {
        setTimeout(() => {
          textareaRef.current.focus();
        }, 100);
      }
    }
  };

  // Quick-reply taps arrive as a window event from Message.js. A ref keeps
  // the listener calling the latest handleSubmit (it closes over state).
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  useEffect(() => {
    const onQuickReply = (e) => {
      if (e.detail?.text) handleSubmitRef.current(null, e.detail.text);
    };
    window.addEventListener(QUICK_REPLY_EVENT, onQuickReply);
    return () => window.removeEventListener(QUICK_REPLY_EVENT, onQuickReply);
  }, []);

  const retryMessage = async (tempId, message, messageText) => {
    dispatch({ 
      type: "UPDATE_MESSAGE_STATUS", 
      payload: { 
        tempId: tempId,
        updates: { status: 'pending' }
      }
    });

    const auth = getAuth();
    const token = await getIdToken(auth.currentUser);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/messages/`,
        {
          method: "POST",
          body: JSON.stringify(message),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      dispatch({ 
        type: "REPLACE_OPTIMISTIC_MESSAGE", 
        payload: { 
          tempId: tempId,
          serverMessage: data
        }
      });

      dispatch({
        type: "UPDATE_LAST_READ_MESSAGE_ID",
        payload: { userId: user.userId, messageId: data._id },
      });

      const messageToReciever = {
        sender: user.userId,
        receiver: currentConversation.members.find(
          (member) => member !== user.userId
        ),
        text: messageText,
        conversationId: currentConversation._id,
        messageId: data._id,
      };

      sendMessage(messageToReciever);

      // Refocus textarea on mobile to keep keyboard active after retry success
      if (isMobile && textareaRef.current) {
        setTimeout(() => {
          textareaRef.current.focus();
        }, 100);
      }

    } catch (err) {
      console.error("Failed to retry message:", err);
      
      dispatch({ 
        type: "UPDATE_MESSAGE_STATUS", 
        payload: { 
          tempId: tempId,
          updates: { 
            status: 'failed',
            error: err.message
          }
        }
      });

      // Refocus textarea on mobile even when retry fails to keep keyboard active
      if (isMobile && textareaRef.current) {
        setTimeout(() => {
          textareaRef.current.focus();
        }, 100);
      }
    }
  };

    // 🚨 Prevent component from rendering too early
  const handleConfirmFindMembers = async () => {
    if (!currentConversation || !community) return;
    setAiLoading(true);
    try {
      const auth = getAuth();
      const token = await getIdToken(auth.currentUser);
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/group-match/invite-bot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        },
        body: JSON.stringify({
          conversationId: currentConversation._id,
          communityId: community.communityId
        })
      });
      dispatch({ type: 'GROUP_BOT_INVITED', payload: { conversationId: currentConversation._id } });
    } catch (err) {
      console.error('[MessengerInput] handleConfirmFindMembers error:', err);
    } finally {
      setAiLoading(false);
      setAiModal(null);
    }
  };

  const isBotConversation = currentConversation?.members?.includes(currentConversation.communityId);
  const showAiBtn = !isBotConversation && (currentConversation?.members?.length || 0) >= 2;

   if (!user || !currentConversation || !community) {
     return null;
    }

  return (
    <>
    <div className="chatMessengerInput">

      {/* AI button + dropdown */}
      {showAiBtn && (
        <div className="ai-input-area">
          <button
            className={`ai-input-btn${aiMenuOpen ? ' open' : ''}`}
            onClick={() => setAiMenuOpen(v => !v)}
            title="AI assistant options"
          >
            ✨ AI
          </button>
          {aiMenuOpen && (
            <div className="ai-input-dropdown">
              <button className="ai-input-option" onClick={() => { setAiMenuOpen(false); setAiModal('find'); }}>
                <span>👥</span> Find others to join the chat
              </button>
              <button className="ai-input-option" onClick={() => { setAiMenuOpen(false); setAiModal('schedule'); }}>
                <span>📅</span> Schedule a time / place
              </button>
            </div>
          )}
        </div>
      )}

      <div className="chatMessengerInput-wrapper">
        <textarea
          ref={textareaRef}
          className="chatMessengerInputTextArea"
          placeholder="Type a message..."
          onChange={handleInputChange}
          value={newMessage}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }
          }}
          onBlur={() => {
            if (isMobile && isActive) {
              setTimeout(() => {
                const focused = document.activeElement;
                // Don't steal focus from vote card inputs or any other interactive element
                if (focused && focused !== document.body && focused !== textareaRef.current) return;
                if (textareaRef.current) {
                  textareaRef.current.focus();
                }
              }, 100);
            }
          }}
          rows="1"
          autoFocus={isMobile}
        ></textarea>
      </div>
      <button
        className="chatSubmitButton"
        onClick={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
        onMouseDown={(e) => {
          if (isMobile) {
            e.preventDefault();
          }
        }}
        disabled={!newMessage.trim()}
        aria-label="Send message"
      >
      </button>
    </div>

    {/* Find members modal */}
    {aiModal === 'find' && (
      <div className="ai-modal-overlay" onClick={() => setAiModal(null)}>
        <div className="ai-modal" onClick={e => e.stopPropagation()}>
          <h3 className="ai-modal-title">Find other people to join the chat</h3>
          <p className="ai-modal-body">
            AI will briefly join this conversation, scan your community for someone who looks like a great match, and send them an invite to join the chat. Once done, AI will leave.
          </p>
          <div className="ai-modal-actions">
            <button className="ai-modal-confirm" onClick={handleConfirmFindMembers} disabled={aiLoading}>
              {aiLoading ? 'Finding...' : 'Yes, find someone'}
            </button>
            <button className="ai-modal-cancel" onClick={() => setAiModal(null)} disabled={aiLoading}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Schedule modal */}
    {aiModal === 'schedule' && (
      <div className="ai-modal-overlay" onClick={() => setAiModal(null)}>
        <div className="ai-modal" onClick={e => e.stopPropagation()}>
          <h3 className="ai-modal-title">Schedule a time / place</h3>
          <p className="ai-modal-body">
            AI will join the conversation and help everyone agree on a time and place to meet up. It will suggest options and coordinate responses from all members.
          </p>
          <div className="ai-modal-actions">
            <button className="ai-modal-confirm" disabled>Coming soon</button>
            <button className="ai-modal-cancel" onClick={() => setAiModal(null)}>Close</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default MessengerInput;


//2025
// 
// // import React, { useState, useEffect } from "react";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { useMessengerContext } from "../../hooks/useMessengerContext";
// import "./MessengerInput.css";
// import useSocket from "../../hooks/useSocket";
// import { initializeApp, getApps } from "firebase/app";
// import { getAuth, getIdToken } from "firebase/auth";
// import firebase from "../../Firebase";
// import Cookies from "js-cookie";

// if (!getApps().length) {
//   initializeApp(firebase);
// }

// const MessengerInput = () => {
//   const [newMessage, setNewMessage] = useState("");
//   const { user } = useAuthContext();
//   const { state, dispatch } = useMessengerContext();
//   const { currentConversation, community } = state;
//   const { sendMessage } = useSocket(user?.userId, currentConversation?._id);
//   const [csrfToken, setCsrfToken] = useState(null); // New state variable for CSRF token
//   const [token, setToken] = useState(null);
//   let controller; // Controller for aborting fetch request

//   // Fetch the CSRF token when the component mounts
//   useEffect(() => {
//     const fetchCsrfToken = async () => {
//       // Fetch the CSRF token from the "mytoken" cookie
//       const csrfToken = Cookies.get("mytoken");
//       setCsrfToken(csrfToken);
//     };

//     fetchCsrfToken();
//   }, []);

//   useEffect(() => {
//     const fetchToken = async () => {
//       const token = await firebase.auth().currentUser.getIdToken(true);
//       setToken(token);
//     };

//     fetchToken();
//   }, []); // Empty dependency array means this effect runs once on component mount

//   const notifyBackend = async (conversationId, messageId, userId) => {
//     // Create an AbortController instance
//     const controller = new AbortController();
//     const { signal } = controller;

//     const newCsrfToken = Cookies.get("mytoken");
//     if (newCsrfToken !== csrfToken) {
//       console.log("CSRF token old:", csrfToken);
//       console.log("CSRF token new:", newCsrfToken);
//       console.log("messageId 2nd api:", messageId);
//       console.log("userId 2nd api:", userId);
//       setCsrfToken(newCsrfToken);
//     }

//     console.log("token", token);
//     console.log("CSRF token new? second api", newCsrfToken);
//     // Define the headers including CSRF token and Authorization token
//     const requestHeaders = {
//       Authorization: `Bearer ${token}`,
//       "X-CSRF-Token": newCsrfToken, // Use the new CSRF token here
//     };

//     try {
//       console.log("second api happening");

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/messages/lastMessageViewed`,

//         {
//           method: "POST", // Assuming you're making a POST request
//           headers: {
//             ...requestHeaders,
//             "Content-Type": "application/json",
//           },
//           // credentials: "include", // To include cookies in the request (if your authentication relies on cookies)
//           signal: signal,
//           body: JSON.stringify({ conversationId, messageId, userId }), // Include all the props in the body
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Network second api response was not ok");
//       }
//       if (response.ok) {
//         console.log("response.ok second api update last message", response.ok);
//       }

//       const data = await response.json();
//       console.log(data); // Process the data
//     } catch (error) {
//       console.error("Error:", error);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     const receiver = currentConversation.members.find(
//       (member) => member !== user.userId
//     );
     
//     const message = {
//       sender: user.userId,
//       receiver: receiver,
//       text: newMessage,
//       conversationId: currentConversation._id,
//       communityId: receiver === community.communityId ? community.communityId : null,
//     };


//     console.log('community888',message)
//     const auth = getAuth();
//     const token = await getIdToken(auth.currentUser);

//     try {
//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/messages/`,
//         {
//           method: "POST",
//           body: JSON.stringify(message),
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//             "X-CSRF-Token": csrfToken, // Include the CSRF token here
//           },
//         }
//       );

//       const data = await response.json();

//       console.log("[messageInput] response data._id", data._id)

//       const messageToReciever = {
//         sender: user.userId,
//         receiver: currentConversation.members.find(
//           (member) => member !== user.userId
//         ),
//         text: newMessage,
//         conversationId: currentConversation._id,
//         messageId: data._id,
//       };

//       sendMessage(messageToReciever);

//       // If there's an ongoing request, abort it
//       if (controller) {
//         controller.abort();
//       }

//       // Start a new request and store the controller
//       notifyBackend(currentConversation._id, data._id, message.receiver);

//       dispatch({ type: "ADD_MESSAGE", payload: data });
//       dispatch({
//         type: "UPDATE_LAST_READ_MESSAGE_ID",
//         payload: { userId: user.userId, messageId: data._id },
//       });

//       // Clear the input box
//       setNewMessage("");
//     } catch (err) {
//       console.error("Failed to send message:", err);
//     }
//   };

//   return (
//     <div className="chatMessengerInput">
//       <textarea
//         className="chatMessengerInputTextArea"
//         placeholder="write something"
//         onChange={(e) => setNewMessage(e.target.value)}
//         value={newMessage}
//         onKeyPress={(e) => {
//           if (e.key === "Enter" && !e.shiftKey) {
//             e.preventDefault(); // Prevents the default action of inserting a newline
//             handleSubmit(e); // Calls the function to send the message
//           }
//         }}
//       ></textarea>
//       <button className="chatSubmitButton" onClick={handleSubmit}>
//         Send
//       </button>
//     </div>
//   );
// };

// export default MessengerInput;

// import React, { useState, useEffect } from 'react';
// import { useAuthContext } from '../../hooks/useAuthContext';
// import { useMessengerContext } from '../../hooks/useMessengerContext';
// import "./MessengerInput.css";
// import useSocket from '../../hooks/useSocket';
// import { initializeApp, getApps } from 'firebase/app';
// import { getAuth, getIdToken } from 'firebase/auth';
// import firebase from '../../Firebase';
// import Cookies from 'js-cookie';

// if (!getApps().length) {
//   initializeApp(firebase);
// }

// const MessengerInput = () => {
//   const [newMessage, setNewMessage] = useState("");
//   const { user } = useAuthContext();
//   const { state, dispatch } = useMessengerContext();
//   const { currentConversation } = state;
//   const { sendMessage } = useSocket(user?.userId, currentConversation?._id);
//   const [csrfToken, setCsrfToken] = useState(null); // New state variable for CSRF token

//    // Fetch the CSRF token when the component mounts
//  useEffect(() => {
//   const fetchCsrfToken = async () => {
//     // Fetch the CSRF token from the "mytoken" cookie
//     const csrfToken = Cookies.get('mytoken');
//     setCsrfToken(csrfToken);
//   };

//   fetchCsrfToken();
// }, []);

// const handleSubmit = async (e) => {
//   e.preventDefault();

//   const message = {
//     sender: user.userId,
//     receiver: currentConversation.members.find(member => member !== user.userId),
//     text: newMessage,
//     conversationId: currentConversation._id,
//   };

//   const auth = getAuth();
//   const token = await getIdToken(auth.currentUser);

//   try {
//     const response = await fetch('api/messages/', {
//       method: 'POST',
//       body: JSON.stringify(message),
//       headers: {
//         'Content-Type': 'application/json',
//         'Authorization': `Bearer ${token}`,
//         'X-CSRF-Token': csrfToken // Include the CSRF token here
//       }
//     });

//     const data = await response.json();

//     const messageToReciever = {
//       sender: user.userId,
//       receiver: currentConversation.members.find(member => member !== user.userId),
//       text: newMessage,
//       conversationId: currentConversation._id,
//       messageId: data._id,
//       csrfToken: csrfToken,
//       token: token,
//     };

//     sendMessage(messageToReciever);

//     dispatch({ type: 'ADD_MESSAGE', payload: data });
//     dispatch({ type: 'UPDATE_LAST_READ_MESSAGE_ID', payload: { userId: user.userId, messageId: data._id } });

//     // Clear the input box
//     setNewMessage('');
//   } catch (err) {
//     console.error('Failed to send message:', err);
//   }
// };

//   return (
//     <div className="chatMessengerInput">
//       <textarea
//         className="chatMessengerInputTextArea"
//         placeholder="write something"
//         onChange={(e) => setNewMessage(e.target.value)}
//         value={newMessage}
//         onKeyPress={(e) => {
//           if (e.key === 'Enter' && !e.shiftKey) {
//             e.preventDefault(); // Prevents the default action of inserting a newline
//             handleSubmit(e); // Calls the function to send the message
//           }
//         }}
//       ></textarea>
//       <button className="chatSubmitButton" onClick={handleSubmit}>Send</button>
//     </div>
//   );
// }

// export default MessengerInput;
